import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux';

type AuctionVehiclesState = {
  page: number;
  perPage: number;
  filters: Record<string, any>;
  openedRelistDialogId: number | null;
  shouldRelistAfterPull: boolean;
  confirmReserveChangeDialogId: number | null;
  selectedReportVehicleIds: number[];
  isCustomReportRowOpen: boolean;
}

export const getOrder = () => {
  const cachedOrder = localStorage.getItem('auctionVehiclesOrder')
  return cachedOrder ?? 'by_time'
}

const setOrder = (order: string) => { 
  localStorage.setItem('auctionVehiclesOrder', order)
}

const initialState: AuctionVehiclesState = {
  page: 1,
  perPage: 24,
  filters: {
    order_by: getOrder()
  },
  openedRelistDialogId: null,
  shouldRelistAfterPull: false,
  confirmReserveChangeDialogId: null,
  selectedReportVehicleIds: [],
  isCustomReportRowOpen: false,
};

const auctionVehiclesSlice = createSlice({
  name: 'auctionVehicles',
  initialState,
  reducers: {
      setPage: (state, action: PayloadAction<number>) => {
        state.page = action.payload;
      },
      setFilters(state, action: PayloadAction<Record<string, any>>) {
        state.filters = action.payload
      },
      setFilterByKey(state, action: PayloadAction<{key: string, value: string}>) {
        const { value, key } = action.payload;
        if (value === '') { 
          delete state.filters[key]
        } else {
          state.filters[key] = value
        }

        if (key === 'order_by') {
          setOrder(value)
        }

        state.page = 1

        // Any filter change can hide a selected vehicle, so drop the selection
        // to avoid sending vehicles that are no longer visible in the list.
        state.selectedReportVehicleIds = []
        if (key === 'lister_id') {
          state.isCustomReportRowOpen = false
        }
      },
      setAuctionId(state, action: PayloadAction<number>) {
        state = initialState 
        state.filters.auction_id = action.payload
      },
      resetFilters(state) {
        state.page = 1
        state.perPage = 24
        state.filters = {
          order_by: getOrder()
        }
        state.selectedReportVehicleIds = []
        state.isCustomReportRowOpen = false
      },
      toggleReportVehicle(state, action: PayloadAction<number>) {
        const id = action.payload
        const index = state.selectedReportVehicleIds.indexOf(id)
        if (index === -1) {
          state.selectedReportVehicleIds.push(id)
        } else {
          state.selectedReportVehicleIds.splice(index, 1)
        }
      },
      setReportVehiclesSelection(state, action: PayloadAction<number[]>) {
        state.selectedReportVehicleIds = action.payload
      },
      clearReportSelection(state) {
        state.selectedReportVehicleIds = []
      },
      setCustomReportRowOpen(state, action: PayloadAction<boolean>) {
        state.isCustomReportRowOpen = action.payload
      },
      setShouldRelistAfterPull(state) {
        state.shouldRelistAfterPull = true
      },
      openRelistDialog(state, action: PayloadAction<number>) {
        state.openedRelistDialogId = action.payload
        state.shouldRelistAfterPull = false
      },
      closeRelistDialog(state) { 
        state.openedRelistDialogId = null
        state.shouldRelistAfterPull = false
      },
      openConfirmReserveChangeDialog(state, action: PayloadAction<number>) {
        state.confirmReserveChangeDialogId = action.payload
      },
      closeConfirmReserveChangeDialog(state) {
        state.confirmReserveChangeDialogId = null
      }
    }
});

export const useIsConfirmReserveDialogOpened = () => {
  const isOpen = useSelector((state: any) => state.auctionVehicles.confirmReserveChangeDialogId as number | null)
  return isOpen
}

export const { setPage, setFilterByKey, resetFilters, setAuctionId, openRelistDialog, closeRelistDialog, setShouldRelistAfterPull, openConfirmReserveChangeDialog, closeConfirmReserveChangeDialog, toggleReportVehicle, setReportVehiclesSelection, clearReportSelection, setCustomReportRowOpen } = auctionVehiclesSlice.actions
export default auctionVehiclesSlice;
